import {
    ETypographyIntents,
    PolymorphicComponentPropsWithRef,
    PolymorphicRef,
} from '@src/typings';
import { VariantProps, cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

const styles = cva('w-full', {
    variants: {
        intent: {
            [ETypographyIntents.H1]: 'font-bold text-2xl',
            [ETypographyIntents.H1Phone]: 'font-bold text-xl',
            [ETypographyIntents.H2]: 'font-medium text-xl',
            [ETypographyIntents.H2Phone]: 'font-bold text-base',
            [ETypographyIntents.H3]: 'font-medium text-base',
            [ETypographyIntents.Action]: 'font-medium text-base',
            [ETypographyIntents.Regular]: 'font-normal text-base',
            [ETypographyIntents.Badge]: 'font-normal text-xs',
            [ETypographyIntents.Label]: 'font-bold text-[8px]',
        },
        align: {
            left: 'text-left',
            center: 'text-center',
            right: 'text-right',
        },
        italic: {
            true: 'italic',
        },
        underline: {
            true: 'underline underline-offset-2',
        },
    },
    defaultVariants: {
        align: 'left',
    },
});

export type TypographyProps<C extends React.ElementType> =
    PolymorphicComponentPropsWithRef<C, VariantProps<typeof styles>>;

const TypographyComponent = forwardRef(
    <C extends React.ElementType = 'span'>(
        {
            as,
            intent,
            align,
            size,
            emphasis,
            italic,
            underline,
            weight,
            className,
            ...props
        }: TypographyProps<C>,
        ref?: PolymorphicRef<C>
    ) => {
        const Component = as || 'span';

        return (
            <Component
                ref={ref}
                className={twMerge(
                    clsx(
                        styles({
                            intent,
                            italic,
                            underline,
                            align,
                            className,
                        })
                    )
                )}
                {...props}
            />
        );
    }
);

TypographyComponent.displayName = 'Typography';

export const Typography = TypographyComponent;
